import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { customerApi, quoteApi } from '../../api/endpoints.ts';
import { ApiRequestError } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import { Button, ConfirmDialog, LoadingBlock, Select, TextInput } from '../ui.tsx';
import type { Customer, QuoteSummary } from '@shared/types/index.ts';

/** A save the editor refused, told apart from anything the Worker answered. */
class UnsavedEditsError extends Error {}

/**
 * "Copy this one and change what is different", offered from wherever a quote
 * is listed or open.
 *
 * The second job most estimating work is: the same three-bedroom terrace, the
 * same move-out clean, the same office block on the next street — priced once
 * and then re-priced for the next person who asks. Rebuilding it room by room
 * is the work the software exists to remove, and the API has been able to copy
 * a quote onto a *different* customer since the endpoint was written. What was
 * missing was a way to say so: the only control was a "duplicate this quote"
 * button on the read-only banner of a closed quote, copying it onto the same
 * customer, and nothing at all on a quote still open.
 *
 * So the copy is asked about rather than simply made. Who the new quote is for
 * is the one thing that most often changes, and it is far cheaper to answer
 * here — one question, before anything exists — than to create the copy
 * against the wrong customer and discover afterwards that a quote's customer
 * is fixed at creation and cannot be moved.
 *
 * Everything else the copy carries (rooms, tasks, minutes, fees, discount,
 * terms, the commercial shape) rides along untouched, because that is the
 * point of copying it; the new draft opens in the editor where the rooms are
 * tweaked.
 */
export function CloneQuoteButton({
  quote,
  variant = 'secondary',
  label,
  className,
  prepare,
}: {
  quote: QuoteSummary;
  variant?: 'primary' | 'secondary';
  /** Overridden where the surrounding prose has already said "copy". */
  label?: string;
  className?: string;
  /**
   * Run before the copy is made, for a caller holding unsaved edits (the
   * editor). The server copies what it has, so an unflushed edit would be
   * missing from the copy and present on the original — the one difference
   * nobody would think to look for. A refused save stops the flow.
   */
  prepare?: () => Promise<QuoteSummary | null>;
}) {
  const { t } = useTranslation();
  const { can } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  /**
   * Who the copy is for. Null means "whoever the source is for" — deliberately
   * not the source's id copied into state, so the label can say *which* choice
   * is the default rather than showing a picked-looking name nobody picked.
   */
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [choosing, setChoosing] = useState(false);
  const [search, setSearch] = useState('');
  /**
   * The address chosen here, `null` meaning "none on the copy". Only read
   * once `propertyChosen`: until then the answer is DERIVED from who the copy
   * is for, so changing the customer moves the address with it rather than
   * leaving the previous one behind to be sent with the new name.
   */
  const [propertyChoice, setPropertyChoice] = useState<string | null>(null);
  const [propertyChosen, setPropertyChosen] = useState(false);

  const customerId = customer?.id ?? quote.customerId;
  const sameCustomer = customerId === quote.customerId;

  const customers = useQuery({
    queryKey: ['customers', { search }],
    queryFn: () => customerApi.list({ q: search, pageSize: 20 }),
    // Only while the picker is actually on screen: this control renders on
    // every row of the quote list, and a list page would otherwise open with
    // twenty-five copies of the same customer search in flight.
    enabled: open && choosing,
  });

  const properties = useQuery({
    queryKey: ['customer-properties', customerId],
    queryFn: () => customerApi.properties(customerId),
    enabled: open,
  });

  /**
   * The address the copy is for.
   *
   * Derived rather than kept in state, because state would have to be
   * corrected after the fact and there is a window before the correction
   * lands. Copying onto somebody else replaces the property with theirs — the
   * source's belongs to the original customer, and the Worker refuses one
   * that does — so a value carried over from the previous answer is not a
   * stale default but a request the server rejects. Their only property is
   * the obvious answer and is taken; with several, the choice is theirs.
   */
  const theirProperties = properties.data?.items;
  const propertyId = propertyChosen
    ? propertyChoice
    : sameCustomer
      ? quote.propertyId
      : theirProperties?.length === 1
        ? theirProperties[0]!.id
        : null;

  /**
   * Confirming into the gap before that lookup lands would answer the address
   * question before the answer arrived: a new customer with exactly one
   * property is about to have it taken, and a copy created a moment earlier
   * gets no address at all — on a quote whose customer is fixed at creation.
   * A failed lookup is not made to wait: the select below is simply empty and
   * the copy takes no address, which is what the Worker would do anyway.
   */
  const awaitingProperties = !sameCustomer && !propertyChosen && properties.isLoading;

  const clone = useMutation({
    mutationFn: async () => {
      if (prepare) {
        const saved = await prepare();
        // A refused save is the editor's own banner to explain; this only has
        // to stop, so nothing is copied from a version the server never got.
        if (!saved) throw new UnsavedEditsError();
      }
      return quoteApi.duplicate(quote.id, {
        // Sent only when it differs, so an unchanged answer keeps the Worker's
        // own handling of a source whose customer has since been deleted.
        ...(sameCustomer ? {} : { customerId }),
        // `null` is a real answer here ("no address on the copy"), so the
        // field is only left out when the source's own property is being kept.
        ...(sameCustomer && propertyId === quote.propertyId ? {} : { propertyId }),
      });
    },
    onSuccess: (copy) => {
      void queryClient.invalidateQueries({ queryKey: ['quotes'] });
      close();
      navigate(`/quotes/${copy.id}`);
    },
  });

  function close() {
    setOpen(false);
    setChoosing(false);
    setSearch('');
    setCustomer(null);
    setPropertyChosen(false);
    setPropertyChoice(null);
    clone.reset();
  }

  // Copying a quote writes a new one, so it is the write permission that
  // decides — the same one the "new quote" button answers to.
  if (!can('quotes:write')) return null;

  const propertyOptions = [
    { value: '', label: t('quotes.clone.noProperty') },
    ...(properties.data?.items ?? []).map((property) => ({
      value: property.id,
      label:
        property.name ??
        ([property.addressLine1, property.city].filter(Boolean).join(', ') || t('quotes.clone.property')),
    })),
  ];

  return (
    <>
      <Button variant={variant} className={className} onClick={() => setOpen(true)}>
        {label ?? t('quotes.clone.action')}
      </Button>

      {open ? (
        <ConfirmDialog
          title={t('quotes.clone.title', { number: quote.quoteNumber })}
          confirmLabel={t('quotes.clone.confirmAction')}
          /*
            Not while the question is still being asked. Under an open
            customer picker the confirm would copy the quote for whoever was
            selected BEFORE the change — the original's customer, most of the
            time — from a dialog that says a change is under way; and a
            quote's customer cannot be corrected afterwards.
          */
          confirmDisabled={choosing || awaitingProperties}
          busy={clone.isPending}
          error={
            clone.isError
              ? clone.error instanceof UnsavedEditsError
                ? t('quotes.clone.saveFailedError')
                : clone.error instanceof ApiRequestError
                  ? clone.error.message
                  : t('quotes.clone.error')
              : null
          }
          onCancel={close}
          onConfirm={() => clone.mutate()}
        >
          <p>{t('quotes.clone.body')}</p>

          <fieldset className="mt-4">
            <legend className="text-sm font-semibold">{t('quotes.clone.customerHeading')}</legend>

            {choosing ? (
              <>
                <TextInput
                  label={t('quotes.clone.searchLabel')}
                  type="search"
                  placeholder={t('quotes.clone.searchPlaceholder')}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />

                {customers.isLoading ? <LoadingBlock label={t('quotes.clone.searching')} /> : null}

                {customers.data && customers.data.items.length > 0 ? (
                  <ul className="mb-3 max-h-56 divide-y divide-[color:var(--color-line)] overflow-y-auto rounded-md border border-[color:var(--color-line)]">
                    {customers.data.items.map((item) => (
                      <li key={item.id}>
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left hover:bg-[color:var(--color-surface)]"
                          onClick={() => {
                            setCustomer(item.id === quote.customerId ? null : item);
                            setChoosing(false);
                            setSearch('');
                            // Whose address this is has just changed, so the
                            // answer goes back to being derived.
                            setPropertyChosen(false);
                            setPropertyChoice(null);
                          }}
                        >
                          <span className="font-medium">{item.displayName}</span>
                          {item.email ? (
                            <span className="block text-sm text-[color:var(--color-muted)]">{item.email}</span>
                          ) : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}

                {customers.data && customers.data.items.length === 0 ? (
                  <p className="mb-3 text-[color:var(--color-muted)]">{t('quotes.clone.noCustomersMatch')}</p>
                ) : null}

                <Button onClick={() => setChoosing(false)}>{t('common.cancel')}</Button>
              </>
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p>
                  {sameCustomer ? quote.customerNameSnapshot : customer!.displayName}
                  {sameCustomer ? (
                    <span className="block text-sm text-[color:var(--color-muted)]">
                      {t('quotes.clone.sameCustomerHint')}
                    </span>
                  ) : null}
                </p>
                <Button onClick={() => setChoosing(true)}>{t('quotes.clone.changeCustomer')}</Button>
              </div>
            )}
          </fieldset>

          {/* Which address the copy is for. Left out while the customer is
              being picked: the list on screen belongs to whoever is selected
              now, and it is about to be replaced by the next choice's. */}
          {!choosing ? (
            <div className="mt-4">
              <Select
                label={t('quotes.clone.propertyLabel')}
                hint={sameCustomer ? undefined : t('quotes.clone.propertyChangedHint')}
                value={propertyId ?? ''}
                onChange={(event) => {
                  setPropertyChoice(event.target.value || null);
                  setPropertyChosen(true);
                }}
                options={propertyOptions}
              />
            </div>
          ) : null}
        </ConfirmDialog>
      ) : null}
    </>
  );
}
